import { Link } from '@/navigation';
import { CoursePicture } from '@/payload/payload-types';
import dayjs from 'dayjs';

import getLanguageShortSuffix from '../../_utilities/get-language-short-suffx';
import { CardBadge } from './CardBadge';
import { shouldShowCourseDiscountCardBadge } from '../../_utilities/should-show-course-card-badge';

import { CourseDuration } from './CourseDuration';
import { Chip } from '../Chip';
import { useTranslations } from 'next-intl';
import { ListCourseType } from '../../_utilities/typescript';
import { PlaceholderValue } from 'next/dist/shared/lib/get-img-props';
import { logToPayload } from '../../_utilities/log-to-payload';
import ImageWithFallback from './ImageWithFallback';

//TODO: remove course price showing logic if EBS decides not to show price indefinetly
export const CourseListCard: React.FC<{
	course: ListCourseType;
	index: number;
}> = ({ course, index }) => {
	const t = useTranslations('Course');
	// const locale = useLocale();

	let earliestCourseVersion = course?.courseversions?.find(
		_courseversion =>
			typeof _courseversion !== 'string' && _courseversion.isAvailableForPurchase,
	);

	if (typeof earliestCourseVersion === 'string') {
		earliestCourseVersion = course?.courseversions?.find(
			_courseversion => typeof _courseversion !== 'string',
		);
	}
	// const trackEvent = useTrackEvent();

	if (!earliestCourseVersion || !course.slug) {
		logToPayload(
			'Courses list err: no course/courseversion. ',
			{
				courseversionId: earliestCourseVersion?.id,
				courseSlug: course.slug,
			},
			'warn',
		);
		return null;
	}

	// const getSortedUniquePrices = () => {
	// 	if (!course?.courseversions) {
	// 		return [];
	// 	}

	// 	return [
	// 		...new Set(
	// 			course.courseversions
	// 				.filter((_courseversion): _courseversion is Courseversion => {
	// 					if (!_courseversion || typeof _courseversion === 'string') {
	// 						console.warn('Courses list err: no course/courseversion. ', {
	// 							courseversionId: _courseversion && extractId(_courseversion),
	// 							courseSlug: course.slug,
	// 						});
	// 						return false;
	// 					}

	// 					return !!_courseversion.isAvailableForPurchase;
	// 				})
	// 				.map(_courseversion => {
	// 					return _courseversion.discountedPrice || _courseversion.price || null;
	// 				}),
	// 		),
	// 	]
	// 		.filter((price): price is number => price !== null)
	// 		.sort((a, b) => a - b);
	// };

	// const sortedUniquePrices = getSortedUniquePrices();
	const hasMultipleVersionsAvailable =
		course?.courseversions &&
		course.courseversions.filter(_courseversion => {
			return (
				_courseversion &&
				typeof _courseversion !== 'string' &&
				!!_courseversion.isAvailableForPurchase
			);
		}).length > 1;
	// const earliestCourseversionPrice =
	// 	earliestCourseVersion.discountedPrice || earliestCourseVersion.price;

	// const priceText =
	// 	sortedUniquePrices.length > 1
	// 		? `${t('starting-from-price')} ${formatPrice(sortedUniquePrices[0], locale, 'EUR')}`
	// 		: `${formatPrice(earliestCourseversionPrice, locale, 'EUR')}
	// 		`;

	const leadingLecturers = earliestCourseVersion.lecturers
		?.filter(_lecturer => _lecturer.isLeading)
		.slice(0, 2);

	const lecturerText = leadingLecturers
		? leadingLecturers
				.map(_lecturer => {
					if (typeof _lecturer.lecturer === 'string' || _lecturer.lecturer === null) {
						logToPayload(`Bad lecturer for course`, {
							courseId: course.id,
							courseversionId:
								typeof earliestCourseVersion !== 'string' &&
								earliestCourseVersion?.id,
							lecturer: _lecturer,
						});
						return null;
					}
					return _lecturer.lecturer.name.trim();
				})
				.filter(Boolean)
				.join(', ')
		: null;

	if (typeof course.picture === 'string') {
		logToPayload(
			`Bad depth coursepicture for course`,
			{
				courseId: course.id,
			},
			'warn',
		);

		return null;
	}

	const courseImage =
		course.picture?.sizes?.listCard?.url ||
		course.picture?.sizes?.card?.url ||
		course.picture.url;

	type BlurProperties = {
		blurDataUrl: string;
		placeholder: PlaceholderValue;
	};
	const blurProperties: BlurProperties = {
		blurDataUrl: '',
		placeholder: 'empty',
	};

	if (course.picture?.blurDataUrl) {
		blurProperties.blurDataUrl = course.picture?.blurDataUrl;
		blurProperties.placeholder = 'blur';
	}

	const startDate = course.earliestStartDate || earliestCourseVersion.timeEdit.startDate;
	const endDate = course.earliestEndDate || earliestCourseVersion.timeEdit.endDate;

	return (
		<Link
			// onClick={() => {
			// 	const courseVersion =
			// 		earliestCourseVersion && typeof earliestCourseVersion !== 'string'
			// 			? earliestCourseVersion
			// 			: null;
			// 	trackEvent('course_tile_clicked', {
			// 		course_name: course.title,
			// 		course_discount_value_eur: getDiscountReduction(courseVersion),
			// 	});
			// }}
			href={{
				pathname: '/courses/[slug]',
				params: {
					slug: course.slug,
				},
			}}
			key={course.id}
			className="group/listcard border-gold-light relative max-h-[358px] w-full max-w-80 cursor-pointer rounded-4xl border md:h-80 md:max-h-full md:max-w-[308px] lg:w-[300px]"
		>
			{/* TODO this will only take into account first courseversion, what if second courseversion has discount? */}
			{shouldShowCourseDiscountCardBadge({ courseversion: earliestCourseVersion }) && (
				<CardBadge
					title={earliestCourseVersion.discountName!}
					subTitle={`${t('until')} ${dayjs(earliestCourseVersion.discountDeadline).format('DD.MM.YYYY')}`}
					color="red"
				/>
			)}

			<div className="relative h-full w-full">
				<div className="bg-muted-navy flex h-full flex-col items-center space-y-4 rounded-4xl md:space-y-6">
					<div className="h-[140px] w-full rounded-4xl bg-slate-200">
						{courseImage && (
							<ImageWithFallback
								//fallbackSrc={`${process.env.NEXT_PUBLIC_SERVER_URL}/assets/no-image.png`}
								alt={(course.picture as CoursePicture)?.alt}
								className="h-full w-full rounded-4xl object-cover"
								src={courseImage}
								width={300}
								height={140}
								placeholder={blurProperties.placeholder}
								blurDataURL={blurProperties.blurDataUrl}
								//unoptimized={true}
								// TODO prioritize first 8 images for faster loading, does it cause flicker?
								priority={index < 8}
								fallbackSrc="/assets/fallback-course-img.jpg"
							/>
						)}
					</div>
					<section className="flex min-h-[120px] w-[262px] flex-col content-between max-[330px]:max-w-[234px] max-[330px]:p-4 md:h-[156px] md:w-[236px]">
						<div className="h-full space-y-1">
							<div className="flex items-center justify-between">
								<CourseDuration
									className="hidden md:block"
									startDate={startDate}
									endDate={endDate}
								></CourseDuration>

								<p className="text-muted-blue mr-0 block text-[10px] font-bold uppercase md:ml-2">
									{getLanguageShortSuffix(earliestCourseVersion.language)}
								</p>
							</div>

							<p className="line-clamp-2 leading-6 font-bold text-white group-hover/listcard:line-clamp-none">
								{course.title}
							</p>
							<div>
								<p className="text-muted-blue text-sm leading-6 font-medium">
									{lecturerText}
								</p>
							</div>
						</div>
						<div className="mb-6 flex items-center justify-between">
							<p className="text-muted-blue text-sm leading-6 font-medium">
								{`${dayjs(startDate).format('DD.MM.YYYY')} - ${dayjs(endDate).format('DD.MM.YYYY')}`}
							</p>
							{hasMultipleVersionsAvailable && (
								<Chip size="xs" bgColor="navy" className="w-fit">
									{t('more-dates')}
								</Chip>
							)}
						</div>

						{/* <Chip size="small" className="mb-8 mt-2 w-fit md:mb-0 md:mt-0">
							{priceText}
						</Chip> */}
					</section>
				</div>

				<div className="absolute inset-0">
					<div className="relative h-full w-full rounded-4xl border-[12px] border-white transition-all delay-100 duration-300 ease-out group-hover/listcard:border-[0px]"></div>
				</div>
			</div>
		</Link>
	);
};
